import React from "react";
import { Box, IconButton, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import {
  Sidebar,
  SidebarList,
  SIDEBAR_RESIZE_HANDLE_WIDTH,
  getSessionDayKey,
} from "./agentSessionsPanelUtils";
import SessionRow, { SessionRowSkeleton } from "./SessionRow";
import { DateDivider } from "./conversation/ActivityIndicators";
import { PAGE_HEADER_HEIGHT_PX } from "../../constants/pageLayout";
import ScheduledTab from "./ScheduledTab";

function ExpandedSidebar({
  agentId,
  agentName,
  sidebarWidth,
  sidebarTab,
  sidebarTabs,
  onSidebarTabChange,
  sessions,
  sessionsLoading,
  sessionsLoadingMore,
  selectedSessionId,
  isNewChat,
  onSelectSession,
  onNewChat,
  onCollapseSidebar,
  showCollapse,
  onSessionsListScroll,
  onSidebarResizeStart,
  showWorkflowProvenanceInChat,
}) {
  return (
    <>
      <Sidebar sx={{ width: `${sidebarWidth}px` }}>
        <Box
          sx={{
            px: 2,
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            height: PAGE_HEADER_HEIGHT_PX,
            boxSizing: "border-box",
          }}
        >
          <Box sx={{ display: "flex", gap: 0, flex: 1 }}>
            {sidebarTabs.map((tab) => (
              <Box
                key={tab.key}
                onClick={() => onSidebarTabChange(tab.key)}
                sx={{
                  px: 1.5,
                  py: 0.5,
                  cursor: "pointer",
                  borderBottom: 2,
                  borderColor:
                    sidebarTab === tab.key ? "text.primary" : "transparent",
                  transition: "all 150ms ease",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: sidebarTab === tab.key ? 600 : 500,
                    color:
                      sidebarTab === tab.key
                        ? "text.primary"
                        : "text.secondary",
                  }}
                >
                  {tab.label}
                </Typography>
              </Box>
            ))}
          </Box>
          {sidebarTab === "sessions" && (
            <IconButton
              onClick={onNewChat}
              size="small"
              sx={{
                color: "text.disabled",
                "&:hover": {
                  backgroundColor: "action.hover",
                  color: "text.primary",
                },
              }}
            >
              <AddIcon sx={{ fontSize: 20 }} />
            </IconButton>
          )}
          {showCollapse && (
            <IconButton
              size="small"
              onClick={onCollapseSidebar}
              sx={{ p: "2px", ml: 0.25, color: "text.secondary" }}
            >
              <ChevronLeftIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Box>

        <SidebarList
          onScroll={
            sidebarTab === "sessions" ? onSessionsListScroll : undefined
          }
        >
          {sidebarTab === "sessions" ? (
            sessionsLoading ? (
              <Box sx={{ py: 1 }}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <SessionRowSkeleton key={i} />
                ))}
              </Box>
            ) : sessions.length === 0 && !isNewChat ? (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography
                  sx={{
                    fontSize: "13px",
                    color: "text.secondary",
                    lineHeight: 1.6,
                  }}
                >
                  No sessions yet. Click + to start a conversation.
                </Typography>
              </Box>
            ) : (
              <>
                {sessions.map((s, index) => {
                  const dayKey = getSessionDayKey(s.createdAt);
                  const prevDayKey =
                    index > 0 ? getSessionDayKey(sessions[index - 1].createdAt) : null;
                  const showDivider = dayKey && dayKey !== prevDayKey;
                  return (
                    <React.Fragment key={s.chatSessionId}>
                      {showDivider && (
                        <DateDivider dateStr={s.createdAt} compact />
                      )}
                      <SessionRow
                        session={s}
                        agentName={agentName}
                        selected={
                          !isNewChat && s.chatSessionId === selectedSessionId
                        }
                        onClick={() => onSelectSession(s.chatSessionId)}
                        showWorkflowProvenanceInChat={
                          showWorkflowProvenanceInChat
                        }
                      />
                    </React.Fragment>
                  );
                })}
                {sessionsLoadingMore && (
                  <Box sx={{ py: 1 }}>
                    {[1, 2].map((i) => (
                      <SessionRowSkeleton key={i} />
                    ))}
                  </Box>
                )}
              </>
            )
          ) : (
            <ScheduledTab agentId={agentId} />
          )}
        </SidebarList>
      </Sidebar>
      <Box
        sx={{
          width: 0,
          flexShrink: 0,
          position: "relative",
          overflow: "visible",
          zIndex: 2,
        }}
      >
        <Box
          onMouseDown={onSidebarResizeStart}
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: "-3px",
            width: `${SIDEBAR_RESIZE_HANDLE_WIDTH}px`,
            cursor: "col-resize",
            "&::after": {
              content: '""',
              position: "absolute",
              top: 0,
              bottom: 0,
              left: "50%",
              transform: "translateX(-50%)",
              width: "1px",
              backgroundColor: "divider",
              pointerEvents: "none",
              transition: "background-color 150ms, width 150ms",
            },
            "&:hover::after": {
              backgroundColor: "text.secondary",
              width: "3px",
              borderRadius: "2px",
            },
          }}
        />
      </Box>
    </>
  );
}

export default React.memo(ExpandedSidebar);
